import { Mesh, Scene, TransformNode, Vector2, Vector3, VertexBuffer, VertexData } from "@babylonjs/core";
import { Cartesian3, Size2, Size3 } from "core/geometry";
import { VirtualDisplayInputsSource } from "./display.inputs.scene";
import { Observable } from "core/events";
import { Length, Quantity } from "core/math";
export var VirtualDisplayUVMode;
(function (VirtualDisplayUVMode) {
    VirtualDisplayUVMode[VirtualDisplayUVMode["KEEP"] = 0] = "KEEP";
    VirtualDisplayUVMode[VirtualDisplayUVMode["STRETCH_U"] = 1] = "STRETCH_U";
    VirtualDisplayUVMode[VirtualDisplayUVMode["STRETCH_V"] = 2] = "STRETCH_V";
    VirtualDisplayUVMode[VirtualDisplayUVMode["STRETCH"] = 3] = "STRETCH";
})(VirtualDisplayUVMode || (VirtualDisplayUVMode = {}));
export class VirtualDisplayOptions {
    constructor() {
        this.uvMode = VirtualDisplayUVMode.KEEP;
    }
}
var UVKind;
(function (UVKind) {
    UVKind[UVKind["MIN_U"] = 0] = "MIN_U";
    UVKind[UVKind["MIN_V"] = 1] = "MIN_V";
    UVKind[UVKind["MAX_U"] = 2] = "MAX_U";
    UVKind[UVKind["MAX_V"] = 3] = "MAX_V";
    UVKind[UVKind["DU"] = 4] = "DU";
    UVKind[UVKind["DV"] = 5] = "DV";
})(UVKind || (UVKind = {}));
export class VirtualDisplay {
    constructor(name, dimension, resolution, scene, unit = Length.Units.meter) {
        this._dimension = Size3.FromSize(dimension);
        this._halfDimension = new Size3(this._dimension.width / 2, this._dimension.height / 2, this._dimension.depth / 2);
        this._resolution = Size3.FromSize(resolution);
        this._ppu = new Vector3(this._resolution.width / this._dimension.width, this._resolution.height / this._dimension.height, this._dimension.depth ? this._resolution.depth / this._dimension.depth : 0);
        this._ratio = new Vector3(this._ppu.x / this._ppu.y, this._ppu.z / this._ppu.y, this._ppu.z / this._ppu.x);
        if (scene == undefined || scene instanceof Scene) {
            this._node = new Mesh(name, scene);
            const data = this._buildVertexData(this._dimension);
            data.applyToMesh(this._node);
            this._center = Cartesian3.Zero();
        }
        else {
            this._node = scene;
            const vertices = scene.getVerticesData(VertexBuffer.PositionKind);
            this._center = vertices ? Cartesian3.Centroid(vertices) : Cartesian3.Zero();
        }
        this._uvs = this._buildUvs(this._node);
        this._worldTransform = new TransformNode(`${name}_context`, this._node.getScene());
        this._worldTransform.parent = this._node;
        this._node.isPickable = true;
        this._inputSource = new VirtualDisplayInputsSource(this);
        this._unit = unit;
    }
    get node() {
        return this._node;
    }
    getScene() {
        return this._node.getScene();
    }
    get propertyChangedObservable() {
        if (!this._propertyChangedObservable) {
            this._propertyChangedObservable = new Observable();
        }
        return this._propertyChangedObservable;
    }
    get width() {
        return this._resolution.width;
    }
    get height() {
        return this._resolution.height;
    }
    get ratio() {
        return this.width / this.height;
    }
    get pointerSource() {
        return this._inputSource;
    }
    _buildUvs(mesh) {
        const uvs = mesh.getVerticesData(VertexBuffer.UVKind);
        if (uvs) {
            let minU = Infinity;
            let minV = Infinity;
            let maxU = -Infinity;
            let maxV = -Infinity;
            for (let i = 0; i < uvs.length; i += 2) {
                const u = uvs[i];
                const v = uvs[i + 1];
                if (u < minU)
                    minU = u;
                if (v < minV)
                    minV = v;
                if (u > maxU)
                    maxU = u;
                if (v > maxV)
                    maxV = v;
            }
            return [minU, minV, maxU, maxV, maxU - minU, maxV - minV];
        }
        return [];
    }
    _buildVertexData(dimension) {
        const data = new VertexData();
        const sx = dimension.width;
        const sy = dimension.height;
        data.positions = [0.5 * sx, -0.5 * sy, 0, 0.5 * sx, 0.5 * sy, 0, -0.5 * sx, 0.5 * sy, 0, -0.5 * sx, -0.5 * sy, 0];
        data.indices = [0, 3, 1, 3, 2, 1];
        data.normals = [0, 0, 1, 0, 0, 1, 0, 0, 1, 0, 0, 1];
        data.uvs = [0, 0, 0, 1, 1, 1, 1, 0];
        return data;
    }
    get context3D() {
        return this._worldTransform;
    }
    get resolution() {
        return this._resolution;
    }
    get unit() {
        return this._unit;
    }
    get dimension() {
        return this._dimension;
    }
    get halfDimension() {
        return this._halfDimension;
    }
    get pixelPerUnit() {
        return this._ppu;
    }
    get dpi() {
        return Quantity.Convert(this._ppu.x, this._unit, Length.Units.inch);
    }
    get aspectRatio() {
        return this._ratio;
    }
    getPixelToRef(pickedCoordinates, pixel) {
        if (!pickedCoordinates) {
            if (pixel) {
                pixel.x = 0;
                pixel.y = 0;
                return pixel;
            }
            return Vector2.Zero();
        }
        pixel = pixel || Vector2.Zero();
        let d = pickedCoordinates.x - this._uvs[UVKind.MIN_U];
        let t = d / this._uvs[UVKind.DU];
        pixel.x = Math.round(t * this._resolution.width);
        d = pickedCoordinates.y - this._uvs[UVKind.MIN_V];
        t = d / this._uvs[UVKind.DV];
        pixel.y = this._resolution.height - Math.round(t * this._resolution.height);
        return pixel;
    }
    getXYZWorldVectors() {
        const transform = this._node.getWorldMatrix();
        const p = this._node.getAbsolutePosition();
        return [
            Vector3.TransformCoordinates(Vector3.Right(), transform).subtractInPlace(p),
            Vector3.TransformCoordinates(Vector3.Up(), transform).subtractInPlace(p),
            Vector3.TransformCoordinates(Vector3.Forward(), transform).subtractInPlace(p),
        ];
    }
    dispose(doNotRecurse, disposeMaterialAndTextures) {
        this._node.dispose(doNotRecurse, disposeMaterialAndTextures);
        this._worldTransform.dispose(doNotRecurse, disposeMaterialAndTextures);
    }
}
VirtualDisplay.QVGA = new Size2(320, 240);
VirtualDisplay.VGA = new Size2(640, 480);
VirtualDisplay.QHD = new Size2(960, 540);
VirtualDisplay.HD = new Size2(1280, 720);
VirtualDisplay.WXGA = new Size2(1280, 800);
VirtualDisplay.FullHD = new Size2(1980, 1080);
VirtualDisplay.UltraHD = new Size2(3840, 2160);
VirtualDisplay.UltraHD2 = new Size2(7680, 4320);
//# sourceMappingURL=display.virtual.js.map